/**
 * NexusMatchConsole - Explainable match score breakdowns per clinician
 * Shows hard-stop details, fit score breakdown with rationale, source data snapshots
 */
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Zap, CheckCircle2, XCircle, Route, Loader2, ChevronDown, ChevronUp,
  MapPin, Clock, Globe, Heart, Users, ShieldCheck
} from "lucide-react";

const FIT_ICONS = {
  availability: Users,
  geography: MapPin,
  specialty: Heart,
  urgency: Clock,
  language: Globe,
  continuity: ShieldCheck,
};

const FIT_COLORS = {
  availability: "text-blue-600 bg-blue-50",
  geography: "text-emerald-600 bg-emerald-50",
  specialty: "text-purple-600 bg-purple-50",
  urgency: "text-amber-600 bg-amber-50",
  language: "text-cyan-600 bg-cyan-50",
  continuity: "text-indigo-600 bg-indigo-50",
};

const NexusMatchConsole = ({
  matchResults,
  selectedClinicians,
  onToggleClinician,
  onRoute,
  routing,
  referralStatus,
}) => {
  const [expandedMatch, setExpandedMatch] = useState(null);

  if (!matchResults || !matchResults.data) return null;

  const passed = matchResults.data.filter(m => m.hard_stop_passed);
  const failed = matchResults.data.filter(m => !m.hard_stop_passed);

  return (
    <Card data-testid="nexus-match-console" className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-purple-600" />
            Match Console
          </CardTitle>
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              {passed.length} eligible
            </Badge>
            {failed.length > 0 && (
              <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                {failed.length} filtered
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Eligible Clinicians */}
        {passed.map((m) => {
          const isSelected = selectedClinicians.includes(m.clinician_id);
          const isExpanded = expandedMatch === m.match_id;

          return (
            <div
              key={m.match_id}
              data-testid={`match-card-${m.clinician_id}`}
              className={`rounded-lg border transition-all ${
                isSelected ? "border-indigo-400 bg-indigo-50/50 shadow-sm" : "border-slate-200 hover:border-indigo-200"
              }`}
            >
              {/* Summary Row */}
              <div
                className="flex items-center gap-3 p-3 cursor-pointer"
                onClick={() => onToggleClinician(m.clinician_id)}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  isSelected ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"
                }`}>
                  #{m.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900">{m.clinician_name}</p>
                  {/* Mini score bars */}
                  <div className="flex gap-1 mt-1.5">
                    {Object.entries(m.fit_scores || {}).map(([key, val]) => {
                      const score = val.score || 0;
                      const weight = val.weight || 0;
                      return (
                        <div key={key} className="flex-1" title={`${key}: ${score} (${weight}%)`}>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-400" : "bg-red-400"}`}
                              style={{ width: `${score}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xl font-bold text-indigo-700">{m.total_score}</p>
                  <p className="text-[10px] text-slate-500">score</p>
                </div>
                <button
                  className="p-1 hover:bg-slate-100 rounded"
                  onClick={(e) => { e.stopPropagation(); setExpandedMatch(isExpanded ? null : m.match_id); }}
                >
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="px-3 pb-3 border-t border-slate-100 pt-3 space-y-3">
                  {/* Hard-stop checks */}
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Hard-Stop Checks</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {Object.entries(m.hard_stop_details || {}).map(([key, val]) => (
                        <div key={key} className={`flex items-center gap-1.5 text-xs p-1.5 rounded ${val.passed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                          {val.passed ? <CheckCircle2 className="w-3 h-3 shrink-0" /> : <XCircle className="w-3 h-3 shrink-0" />}
                          <span className="truncate">{key.replace(/_/g, " ")}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Fit score breakdown */}
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Fit Scores</p>
                    <div className="space-y-2">
                      {Object.entries(m.fit_scores || {}).map(([key, val]) => {
                        const Icon = FIT_ICONS[key] || Zap;
                        const colors = FIT_COLORS[key] || "text-slate-600 bg-slate-50";
                        return (
                          <div key={key} className="flex items-center gap-2">
                            <div className={`p-1 rounded ${colors}`}>
                              <Icon className="w-3 h-3" />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between text-xs mb-0.5">
                                <span className="capitalize font-medium text-slate-700">{key}</span>
                                <span className="text-slate-500">{val.score}/{100} ({val.weight}% wt)</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${val.score >= 70 ? "bg-emerald-500" : val.score >= 40 ? "bg-amber-400" : "bg-red-400"}`}
                                  style={{ width: `${val.score}%` }}
                                />
                              </div>
                              {val.rationale && (
                                <p className="text-[10px] text-slate-500 mt-0.5">{val.rationale}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Source snapshot */}
                  {m.snapshot && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Data Snapshot</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">Caseload</span>
                          <p className="font-medium">{m.snapshot.clinician_caseload || 0} / {m.snapshot.clinician_max_caseload || 25}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">Waitlist</span>
                          <p className="font-medium">{m.snapshot.waitlist_count || 0}</p>
                        </div>
                        {m.snapshot.auth_units_remaining !== undefined && (
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Auth Units</span>
                            <p className="font-medium">{m.snapshot.auth_units_remaining}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Route action */}
        {passed.length > 0 && referralStatus !== "placed" && (
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button
              size="sm"
              onClick={onRoute}
              disabled={routing || selectedClinicians.length === 0}
              data-testid="route-referral-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {routing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Route className="w-3.5 h-3.5 mr-1" />}
              Route to Selected ({selectedClinicians.length})
            </Button>
          </div>
        )}

        {/* Failed hard-stops (collapsible) */}
        {failed.length > 0 && (
          <details className="mt-2">
            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
              {failed.length} clinician{failed.length !== 1 ? "s" : ""} filtered out by hard-stops
            </summary>
            <div className="mt-2 space-y-1.5">
              {failed.map(m => (
                <div key={m.match_id} className="text-xs p-2.5 bg-red-50/50 rounded-lg border border-red-100">
                  <span className="font-medium text-slate-700">{m.clinician_name}</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Object.entries(m.hard_stop_details || {}).filter(([, v]) => !v.passed).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-[10px] border-red-200 text-red-600">
                        {k.replace(/_/g, " ")}: {v.reason}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

export default NexusMatchConsole;
